import { SafeImage } from "./SafeImage";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import { api, fmtPrice } from "../lib/api";

/**
 * SearchBox - Busca ao vivo de produtos com dropdown de resultados.
 * Debounce 300ms + fecha ao clicar fora + navegação por teclado.
 */
export const SearchBox = ({ className = "", onNavigate }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(-1);
    const boxRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    // Debounce input
    useEffect(() => {
        const id = setTimeout(() => setDebounced(query.trim()), 300);
        return () => clearTimeout(id);
    }, [query]);

    // Fetch results
    useEffect(() => {
        if (!debounced) { setResults([]); return; }
        let cancelled = false;
        setLoading(true);
        api.get("/products", { params: { q: debounced, limit: 8 } })
            .then(({ data }) => { if (!cancelled) { setResults(data || []); setActive(-1); } })
            .catch(() => { if (!cancelled) setResults([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [debounced]);

    // Click outside to close
    useEffect(() => {
        const onDown = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    const go = (slug) => {
        setOpen(false);
        setQuery("");
        onNavigate?.();
        navigate(`/produtos/${slug}`);
    };

    const goAll = () => {
        if (!query.trim()) return;
        setOpen(false);
        onNavigate?.();
        navigate(`/produtos?q=${encodeURIComponent(query.trim())}`);
        setQuery("");
    };

    const onKey = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
        else if (e.key === "Enter") { e.preventDefault(); if (active >= 0 && results[active]) go(results[active].slug); else goAll(); }
        else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
    };

    const showDropdown = open && (loading || query.trim().length > 0);

    return (
        <div ref={boxRef} className={`relative ${className}`} data-testid="search-box">
            <div className={`flex items-center gap-2 border-2 transition-all duration-200 rounded-full pl-4 pr-1 py-1 ${open ? "border-brand-ink bg-white" : "border-brand-border bg-white/80"}`}>
                <Search className="w-4 h-4 text-brand-inkSoft flex-shrink-0" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKey}
                    placeholder="Buscar produtos…"
                    data-testid="search-input"
                    className="flex-1 bg-transparent outline-none text-sm py-1.5 placeholder:text-brand-inkSoft"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => { setQuery(""); inputRef.current?.focus(); }}
                        data-testid="search-clear"
                        className="w-7 h-7 inline-flex items-center justify-center hover:bg-brand-bg rounded-full flex-shrink-0"
                        aria-label="Limpar"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-brand-border rounded-sm shadow-2xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto" data-testid="search-results">
                    {loading && (
                        <div className="px-4 py-6 text-center text-sm text-brand-inkSoft flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Buscando…
                        </div>
                    )}

                    {!loading && results.length === 0 && debounced && (
                        <div className="px-4 py-6 text-center">
                            <div className="text-sm font-semibold text-brand-ink mb-1">Nada encontrado</div>
                            <div className="text-xs text-brand-inkSoft">Nenhum produto para "{debounced}"</div>
                        </div>
                    )}

                    {!loading && results.map((p, i) => (
                        <button
                            key={p.id}
                            type="button"
                            onMouseEnter={() => setActive(i)}
                            onClick={() => go(p.slug)}
                            data-testid={`search-result-${p.slug}`}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-brand-border/50 last:border-0 text-left transition-colors ${active === i ? "bg-brand-bg" : "hover:bg-brand-bg/60"}`}
                        >
                            <div className="w-12 h-12 flex-shrink-0 bg-brand-bg rounded-sm overflow-hidden">
                                <SafeImage src={p.images?.[0]} alt="" loading="lazy" className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-brand-ink truncate">{p.name}</div>
                                <div className="text-xs text-brand-inkSoft truncate">{p.category_slug}</div>
                            </div>
                            <div className="text-sm font-bold text-brand-gold flex-shrink-0">{fmtPrice(p.price)}</div>
                        </button>
                    ))}

                    {!loading && results.length > 0 && (
                        <button
                            type="button"
                            onClick={goAll}
                            data-testid="search-see-all"
                            className="w-full text-center text-xs font-bold uppercase tracking-wider text-brand-ink hover:bg-brand-ink hover:text-white py-3 border-t border-brand-border transition-colors"
                        >
                            Ver todos os resultados para "{query}"
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBox;
